import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";

import { ApiError, type Me, api } from "./api/client";
import { ActivityLogRoute } from "./screens/ActivityLog";
import { DocumentBody } from "./screens/DocumentBody";
import { DocumentScreen } from "./screens/DocumentScreen";
import { HistoryRoute } from "./screens/History";
import { Library } from "./screens/Library";
import { ResearchRoute } from "./screens/Research";
import { Settings } from "./screens/Settings";
import { SignIn } from "./screens/SignIn";
import { SpikePage } from "./screens/SpikePage";
import { claimCache, rememberMe, rememberedMe } from "./storage/me";

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/signin" element={<SignIn />} />
        <Route path="/spike" element={<SpikePage />} />
        <Route element={<RequireSignIn />}>
          <Route path="/" element={<Library />} />
          {/* The panels are nested, not adjacent: /d/:id stays matched while
              one is open, so the document's store, outbox and event stream are
              never torn down to look at its history. */}
          <Route path="/d/:id" element={<DocumentScreen />}>
            <Route index element={<DocumentBody />} />
            <Route path="research" element={<ResearchRoute />} />
            <Route path="research/log" element={<ActivityLogRoute />} />
            <Route path="history" element={<HistoryRoute />} />
            {/* "." is the parent, /d/:id — a mistyped panel is still a document. */}
            <Route path="*" element={<Navigate to="." replace />} />
          </Route>
          <Route path="/settings" element={<Settings />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

type Gate = { kind: "checking" } | { kind: "in"; me: Me } | { kind: "out" };

/** The gate must not depend on the network.
 *
 *  Only the server can say a session is invalid, and it says so with a 401.
 *  Being unable to ask is a different thing entirely, and treating it as
 *  signed out would put an offline error page in front of documents this
 *  device already has. So an unreachable server lets someone through on the
 *  strength of the last answer it gave; if the session really has expired,
 *  the first request that reaches the server says so and sends them to
 *  sign in.
 */
function RequireSignIn() {
  const [gate, setGate] = useState<Gate>(() => {
    const remembered = rememberedMe();
    return remembered ? { kind: "in", me: remembered } : { kind: "checking" };
  });

  useEffect(() => {
    let cancelled = false;
    api
      .get<Me>("/api/me")
      .then((me) => {
        if (cancelled) return;
        rememberMe(me);
        claimCache(me.email);
        setGate({ kind: "in", me });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 401) {
          rememberMe(null);
          setGate({ kind: "out" });
          return;
        }
        // Unreachable, not unauthorised.
        const remembered = rememberedMe();
        if (remembered) setGate({ kind: "in", me: remembered });
        else setGate({ kind: "out" });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (gate.kind === "checking") return null;
  if (gate.kind === "out") return <Navigate to="/signin" replace />;
  return <Outlet context={gate.me} />;
}
